import { type } from 'arktype';

/**
 * Layer of a rule instance (mirrors `timetracking.rule_layer`, F20/spec §3f):
 * `statutory` (country law) < `gav` (industry contract package) < `reglement`
 * (self-created house rules). Only `reglement` is deactivatable.
 */
export type RuleLayer = 'statutory' | 'gav' | 'reglement';

/** Compliance dimension of a rule kind (grouping in the catalog UI). */
export type RuleCatalogDimension = 'REST' | 'BREAK' | 'WORKTIME' | 'SURCHARGE' | 'SUNDAY';

/** Direction of a monotonicity-constrained parameter. */
export type MonotonicityDirection = 'lowerIsStricter' | 'higherIsStricter';

/**
 * Body of `POST /time-tracking/rule-instances/validate` and
 * `POST /time-tracking/rule-instances` — a candidate reglement instance.
 * `confirmDuplicateWarning` (create only, story 36-4): acknowledges an
 * overlapping-surcharge-window duplicate WARNING — a duplicate BLOCK can
 * never be confirmed away.
 */
export const ruleInstanceCandidateSchema = type({
  kindKey: 'string > 0',
  params: 'Record<string, unknown>',
  'confirmDuplicateWarning?': 'boolean',
});
export type RuleInstanceCandidate = typeof ruleInstanceCandidateSchema.t;

/**
 * Body of `PATCH /time-tracking/rule-instances/:id/deactivate` (story 36-4):
 * deactivation is destructive for compliance coverage, so the server demands
 * an explicit confirmation — the UI collects it via a dialog.
 */
export const ruleInstanceDeactivateSchema = type({
  confirm: 'boolean',
});
export type RuleInstanceDeactivateInput = typeof ruleInstanceDeactivateSchema.t;

/** Body of `POST /time-tracking/rule-instances/preview` (dry run, default 90 days). */
export const ruleInstancePreviewSchema = type({
  kindKey: 'string > 0',
  params: 'Record<string, unknown>',
  'days?': '1 <= number.integer <= 365',
});
export type RuleInstancePreviewInput = typeof ruleInstancePreviewSchema.t;

/** One parameter that violates the monotonicity against a lower layer (F20b). */
export interface MonotonicityViolation {
  param: string;
  direction: MonotonicityDirection;
  candidateValue: number;
  /** Bound value of the violated lower-layer instance (field name is historic). */
  statutoryValue: number;
  statutoryInstanceId: string;
  /** Layer of the violated bound — absent in legacy fixtures ⇒ statutory. */
  boundLayer?: RuleLayer;
  /** i18n key of the plain-text explanation; params = this violation object. */
  messageKey: string;
}

/** How an existing active instance duplicates the candidate (story 36-4, U9). */
export type RuleDuplicateMatchType = 'identicalParams' | 'identicalWindow' | 'overlappingWindow';

/** One existing active rule instance the candidate would duplicate. */
export interface RuleDuplicateMatch {
  instanceId: string;
  kindKey: string;
  layer: RuleLayer;
  params: Record<string, unknown>;
  effectiveFrom: string;
  matchType: RuleDuplicateMatchType;
  severity: 'block' | 'warning';
}

/**
 * Duplicate gate result (story 36-4): `block` = an equivalent rule is already
 * active (same kind, overlapping validity, equivalent params — or an identical
 * surcharge window on the same balance); `warning` = a surcharge window merely
 * OVERLAPS an active window on the same balance (double-credit risk,
 * confirmable).
 */
export interface RuleDuplicateCheckResult {
  status: 'ok' | 'warning' | 'block';
  matches: RuleDuplicateMatch[];
}

export interface ValidateRuleInstanceResult {
  ok: boolean;
  violations: MonotonicityViolation[];
  /** Duplicate gate (story 36-4) — absent only in legacy fixtures. */
  duplicate?: RuleDuplicateCheckResult;
}

/** One example case the dry run would newly flag. */
export interface DryRunExample {
  personId: string;
  firstName: string;
  lastName: string;
  evaluationDate: string;
  severity: 'warn' | 'block';
  computedValues: Record<string, number | string>;
}

/**
 * Why a preview is not a clean, complete assessment (D07): `PREVIEW_TRUNCATED`
 * = more than the pair bound and only the first batch was evaluated;
 * `INVALID_RULE_PARAMS` = a baseline rule could not be evaluated and was
 * filtered; `MISSING_OBLIGATION_REFERENCE` = a candidate posting has no
 * baseline obligation to delta against.
 */
export type DryRunDiagnosticCode = 'PREVIEW_TRUNCATED' | 'INVALID_RULE_PARAMS' | 'MISSING_OBLIGATION_REFERENCE';

export interface DryRunDiagnostic {
  code: DryRunDiagnosticCode;
  ruleInstanceId?: string;
}

/** Per-person obligation change the candidate would introduce (D07). */
export interface DryRunObligationDelta {
  personId: string;
  balanceTypeKey: string;
  deltaHours: number;
}

/** Read-only impact preview of a candidate rule over the last N days. */
export interface DryRunResult {
  days: number;
  evaluatedDays: number;
  newlyFlagged: number;
  affectedPersons: number;
  examples: DryRunExample[];
  /** Absent in legacy responses ⇒ complete. */
  completeness?: 'complete' | 'incomplete';
  diagnostics?: DryRunDiagnostic[];
  obligationDeltas?: DryRunObligationDelta[];
}

/** Catalog entry: a rule instance enriched with its kind metadata. */
export interface RuleCatalogInstance {
  id: string;
  kindKey: string;
  params: Record<string, unknown>;
  layer: RuleLayer;
  jurisdiction: string;
  version: number;
  effectiveFrom: string;
  effectiveTo: string | null;
  isActive: boolean;
  /** Preset package the instance came from (F20c, e.g. 'l-gav-gastro'); null otherwise. */
  sourceKey: string | null;
  /** null when no RuleKind is registered for the kindKey (misconfiguration). */
  dimension: RuleCatalogDimension | null;
  /** Params that can be adjusted on the reglement layer, with their strictness direction. */
  adjustableParams: { param: string; direction: MonotonicityDirection }[];
}

/** One rule of a GAV preset package (F20c). */
export interface GavPresetRule {
  kindKey: string;
  params: Record<string, unknown>;
}

/**
 * Curated GAV preset (F20c, spec §3f.32): activating it creates `gav`-layer
 * instances carrying `sourceKey = key`. `active` = at least one active gav
 * instance with this sourceKey exists.
 */
export interface GavPresetSummary {
  key: string;
  jurisdiction: string;
  active: boolean;
  rules: GavPresetRule[];
}

/**
 * Body of `POST /time-tracking/rule-instances/gav-presets/:key/activate`
 * (F21, spec §3f.34): optional subset of the preset's rules — the wizard
 * switches decide BEFORE activation which optional GAV rules apply; after
 * activation the package is locked.
 */
export const gavPresetActivateSchema = type({
  'kindKeys?': 'string[]',
});
export type GavPresetActivateInput = typeof gavPresetActivateSchema.t;

/** Result of `GET /time-tracking/rule-instances/setup-status` (F21). */
export interface RuleSetupStatus {
  /** True once the setup wizard was completed (setting `timetracking.ruleSetupCompleted`). */
  completed: boolean;
  statutoryActive: number;
  gavActive: number;
  reglementActive: number;
}
